/**
 * クライアントIPの検証と正規化。
 *
 * `clientIp()` はヘッダ由来の文字列をそのまま返すため、`"unknown"` や偽装値、
 * ポート付き表記が混ざりうる。BAN判定へ渡す前にここで一度だけ検証し、
 * 以降は検証済みの型として扱う。
 */

/** 検証済みのクライアントIP。生の文字列と区別するため専用型で持つ。 */
export type ClientAddress = {
  /** PostgreSQL の inet/cidr へ渡せる正規化済み表記 */
  readonly value: string
  readonly family: "ipv4" | "ipv6"
}

const IPV4_PATTERN = /^(\d{1,3})\.(\d{1,3})\.(\d{1,3})\.(\d{1,3})$/

function parseIpv4(input: string): ClientAddress | null {
  const matched = IPV4_PATTERN.exec(input)
  if (!matched) {
    return null
  }
  const octets = matched.slice(1, 5)
  for (const octet of octets) {
    // 先頭0埋めは実装ごとに8進数解釈が割れるため受け付けない
    if (octet.length > 1 && octet.startsWith("0")) {
      return null
    }
    if (Number(octet) > 255) {
      return null
    }
  }
  return { value: octets.join("."), family: "ipv4" }
}

/**
 * IPv6を検証する。`::` 短縮とIPv4-mapped (`::ffff:192.0.2.1`) を受け付ける。
 *
 * 表記ゆれを残したままDBへ渡すと `cidr` 側の正規化に依存するため、
 * 受理可否だけをここで確定し、正規化はPostgreSQLに委ねる。
 */
function parseIpv6(input: string): ClientAddress | null {
  // ゾーンID (`fe80::1%eth0`) は経路情報でありBAN対象の識別子にならないため除去する
  const withoutZone = input.split("%")[0] ?? ""
  if (withoutZone.length === 0 || !withoutZone.includes(":")) {
    return null
  }
  const doubleColonCount = withoutZone.split("::").length - 1
  if (doubleColonCount > 1) {
    return null
  }
  const [head = "", tail = ""] = doubleColonCount === 1 ? withoutZone.split("::") : [withoutZone, ""]
  const headGroups = head.length > 0 ? head.split(":") : []
  const tailGroups = tail.length > 0 ? tail.split(":") : []
  const groups = [...headGroups, ...tailGroups]
  // 末尾のIPv4-mapped表記は32bit分、つまり2グループを占める
  const lastGroup = groups[groups.length - 1]
  const embeddedIpv4 = lastGroup?.includes(".") === true ? lastGroup : null
  if (embeddedIpv4 !== null) {
    if (parseIpv4(embeddedIpv4) === null || !withoutZone.endsWith(embeddedIpv4)) {
      return null
    }
    groups.pop()
  }
  const hasEmbeddedIpv4 = embeddedIpv4 !== null
  for (const group of groups) {
    if (group.length === 0 || group.length > 4 || !/^[0-9a-fA-F]+$/.test(group)) {
      return null
    }
  }
  const occupied = groups.length + (hasEmbeddedIpv4 ? 2 : 0)
  if (doubleColonCount === 1 ? occupied > 7 : occupied !== 8) {
    return null
  }
  const headHex = headGroups.filter((group) => !group.includes(".")).map((group) => Number.parseInt(group, 16))
  const tailHex = tailGroups.filter((group) => !group.includes(".")).map((group) => Number.parseInt(group, 16))
  const embeddedGroups = embeddedIpv4
    ? embeddedIpv4
        .split(".")
        .map(Number)
        .reduce<number[]>((result, octet, index, octets) => {
          if (index % 2 === 0) {
            result.push(octet * 256 + (octets[index + 1] ?? 0))
          }
          return result
        }, [])
    : []
  const omittedCount = doubleColonCount === 1 ? 8 - headHex.length - tailHex.length - embeddedGroups.length : 0
  const expanded = [...headHex, ...Array.from({ length: omittedCount }, () => 0), ...tailHex, ...embeddedGroups]
  let longestStart = -1
  let longestLength = 0
  for (let start = 0; start < expanded.length; ) {
    if (expanded[start] !== 0) {
      start += 1
      continue
    }
    let end = start + 1
    while (expanded[end] === 0) {
      end += 1
    }
    if (end - start > longestLength) {
      longestStart = start
      longestLength = end - start
    }
    start = end
  }
  const hexadecimal = expanded.map((group) => group.toString(16))
  const value =
    longestLength >= 2
      ? `${hexadecimal.slice(0, longestStart).join(":")}::${hexadecimal.slice(longestStart + longestLength).join(":")}`
      : hexadecimal.join(":")
  return { value, family: "ipv6" }
}

/**
 * クライアントIP文字列を検証済みアドレスへ正規化する。
 *
 * 受理できない場合は `null` を返す。`null` は「アドレスBAN判定不能」であり、
 * 拒否するか通すかは呼び出し側の方針に委ねる (アドレスBANは fail-open)。
 */
export function parseClientAddress(input: string | null | undefined): ClientAddress | null {
  if (typeof input !== "string") {
    return null
  }
  const trimmed = input.trim()
  // 括弧・コロン・5桁ポートを含む場合に45文字のIPv6を収める上限。
  if (trimmed.length === 0 || trimmed.length > 53) {
    return null
  }
  // `[2001:db8::1]:443` のようなブラケット付きポート表記を剥がす
  const bracketed = /^\[([^\]]{1,45})\](?::\d{1,5})?$/.exec(trimmed)
  if (bracketed?.[1]) {
    return parseIpv6(bracketed[1])
  }
  if (trimmed.length > 45) {
    return null
  }
  // IPv4のポート付き表記 (`192.0.2.1:443`)。IPv6はコロンを含むため除外する
  const ipv4WithPort = /^(\d{1,3}(?:\.\d{1,3}){3}):\d{1,5}$/.exec(trimmed)
  if (ipv4WithPort?.[1]) {
    return parseIpv4(ipv4WithPort[1])
  }
  return trimmed.includes(":") ? parseIpv6(trimmed) : parseIpv4(trimmed)
}

/**
 * punish account のシグナルに使う IP 接頭辞キー。IPv4 は /32、IPv6 は /64 に丸める。
 * IPv4-mapped IPv6 は IPv4 として扱う。受理できないアドレスは `null`。
 */
export function ipPrefixKey(input: string | null | undefined): string | null {
  const parsed = parsePostRateLimitAddress(input)
  if (parsed === null) {
    return null
  }
  if (parsed.family === "ipv4") {
    return `${parsed.value}/32`
  }
  const [head = "", tail = ""] = parsed.value.includes("::") ? parsed.value.split("::") : [parsed.value, ""]
  const headGroups = head.length > 0 ? head.split(":") : []
  const tailGroups = tail.length > 0 ? tail.split(":") : []
  const omitted = Math.max(0, 8 - headGroups.length - tailGroups.length)
  const groups = [...headGroups, ...Array.from({ length: omitted }, () => "0"), ...tailGroups]
  return `${groups.slice(0, 4).join(":")}::/64`
}

/** 投稿間隔制限ではIPv4-mapped IPv6を同じIPv4クライアントとして扱う。 */
export function parsePostRateLimitAddress(input: string | null | undefined): ClientAddress | null {
  const parsed = parseClientAddress(input)
  if (parsed?.family !== "ipv6") {
    return parsed
  }
  const mapped = /^::ffff:([0-9a-f]{1,4}):([0-9a-f]{1,4})$/.exec(parsed.value)
  if (!mapped?.[1] || !mapped[2]) {
    return parsed
  }
  const high = Number.parseInt(mapped[1], 16)
  const low = Number.parseInt(mapped[2], 16)
  return {
    value: `${high >> 8}.${high & 0xff}.${low >> 8}.${low & 0xff}`,
    family: "ipv4",
  }
}

/** IP範囲BANで許可する最短prefix。正規化と許可範囲の検証は分ける。 */
export const MIN_BAN_PREFIX_LENGTH = { ipv4: 24, ipv6: 48 } as const

export type NormalizedCidr = {
  value: string
  family: "ipv4" | "ipv6"
  prefixLength: number
}

/** BAN範囲をIPv4 /32・IPv6 /64を既定として正規化する。mappedはIPv4尺度のprefixを使う。 */
export function normalizeCidr(input: string): NormalizedCidr | null {
  const trimmed = input.trim()
  const separatorIndex = trimmed.lastIndexOf("/")
  const addressPart = separatorIndex === -1 ? trimmed : trimmed.slice(0, separatorIndex)
  const prefixPart = separatorIndex === -1 ? null : trimmed.slice(separatorIndex + 1)
  const parsed = parsePostRateLimitAddress(addressPart)
  if (parsed === null) {
    return null
  }
  const maxPrefixLength = parsed.family === "ipv4" ? 32 : 128
  let prefixLength = parsed.family === "ipv4" ? 32 : 64
  if (prefixPart !== null) {
    if (!/^\d{1,3}$/.test(prefixPart)) {
      return null
    }
    prefixLength = Number(prefixPart)
    if (prefixLength > maxPrefixLength) {
      return null
    }
  }
  if (parsed.family === "ipv4") {
    const octets = parsed.value.split(".").map(Number)
    const value =
      (((octets[0] ?? 0) << 24) | ((octets[1] ?? 0) << 16) | ((octets[2] ?? 0) << 8) | (octets[3] ?? 0)) >>> 0
    const mask = prefixLength === 0 ? 0 : (0xffffffff << (32 - prefixLength)) >>> 0
    const network = (value & mask) >>> 0
    return {
      value: `${[(network >>> 24) & 255, (network >>> 16) & 255, (network >>> 8) & 255, network & 255].join(".")}/${prefixLength}`,
      family: "ipv4",
      prefixLength,
    }
  }
  const [head = "", tail = ""] = parsed.value.includes("::") ? parsed.value.split("::") : [parsed.value, ""]
  const headGroups = head ? head.split(":") : []
  const tailGroups = tail ? tail.split(":") : []
  const groups = [
    ...headGroups,
    ...Array.from({ length: 8 - headGroups.length - tailGroups.length }, () => "0"),
    ...tailGroups,
  ]
  const masked = groups
    .map((group, index) => {
      const keptBits = Math.max(0, Math.min(16, prefixLength - index * 16))
      const mask = keptBits === 0 ? 0 : (0xffff << (16 - keptBits)) & 0xffff
      return (Number.parseInt(group, 16) & mask).toString(16)
    })
    .join(":")
  const network = parseClientAddress(masked)
  return network ? { value: `${network.value}/${prefixLength}`, family: "ipv6", prefixLength } : null
}
